'use client';

import { useEffect, useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { aiUsageApi, TokenRow } from '../_lib/api';

export default function AdminTokensPage() {
  const { t } = useTranslation();
  const tt = t.aiUsage;
  const [status, setStatus] = useState<'all' | 'active' | 'revoked'>('all');
  const [items, setItems] = useState<TokenRow[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const res = await aiUsageApi.listTokens({ status, page: 1, pageSize: 100 });
      setItems(res.items);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, [status]);

  const revoke = async (id: string) => {
    const reason = prompt(tt.token.revokeReasonLabel);
    if (reason === null) return;
    await aiUsageApi.adminRevokeToken(id, reason);
    await reload();
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-2xl font-semibold mb-6">{tt.nav.tokens}</h1>

      <div className="flex gap-2 mb-4 text-sm">
        {(['all', 'active', 'revoked'] as const).map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={`px-3 py-1 rounded ${status === s ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}
          >
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : (
        <div className="bg-white rounded shadow">
          <table className="w-full text-sm">
            <thead className="text-gray-500 border-b">
              <tr>
                <th className="py-3 px-4 text-left">{tt.token.columnName}</th>
                <th className="py-3 px-4 text-left">{tt.token.columnPrefix}</th>
                <th className="py-3 px-4 text-left">User</th>
                <th className="py-3 px-4 text-left">{tt.token.columnLastUsed}</th>
                <th className="py-3 px-4 text-left">{tt.token.columnStatus}</th>
                <th className="py-3 px-4 text-right">{tt.token.columnActions}</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={6} className="py-8 text-center text-gray-400">No tokens</td></tr>
              )}
              {items.map((tk) => (
                <tr key={tk.id} className="border-b">
                  <td className="py-3 px-4">{tk.name}</td>
                  <td className="py-3 px-4 font-mono text-xs">{tk.prefix}...</td>
                  <td className="py-3 px-4 text-xs">
                    {tk.user ? `${tk.user.displayName} (${tk.user.email})` : '-'}
                  </td>
                  <td className="py-3 px-4 text-xs text-gray-500">
                    {tk.lastUsedAt ? new Date(tk.lastUsedAt).toLocaleString() : '-'}
                  </td>
                  <td className="py-3 px-4">
                    {tk.revokedAt ? (
                      <span className="text-red-600 text-xs">{tt.token.statusRevoked}</span>
                    ) : (
                      <span className="text-green-600 text-xs">{tt.token.statusActive}</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right">
                    {!tk.revokedAt && (
                      <button onClick={() => revoke(tk.id)} className="text-xs text-red-600 hover:underline">
                        {tt.token.actionRevoke}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
